import { AstronautMoonLottie } from '@sohanscript/ui/animated/astronaut-moon-lottie';
import { AstronautReadingLottie } from '@sohanscript/ui/animated/astronaut-reading-lottie';
import { Backlight } from '@sohanscript/ui/animated/backlight';
import { Motion } from '@sohanscript/ui/motion';
import { createFileRoute } from '@tanstack/react-router';
import { domAnimation, LazyMotion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Contact } from '#/components/contact.tsx';
import { READY_CHANGE_EVENT } from '#/components/contact-form.tsx';

export const Route = createFileRoute('/_main/start')({
	component: RouteComponent,
	validateSearch: z.object({
		ready: z.literal('true').optional().catch(undefined),
		service: z.string().optional().catch(undefined),
	}),
});

function readReadyParam(): 'true' | undefined {
	if (typeof window === 'undefined') return undefined;
	return new URLSearchParams(window.location.search).get('ready') === 'true'
		? 'true'
		: undefined;
}

function RouteComponent() {
	const search = Route.useSearch();
	const reduceMotion = useReducedMotion();
	const [liveReady, setLiveReady] = useState(search.ready);

	useEffect(() => {
		setLiveReady(search.ready);
	}, [search.ready]);

	useEffect(() => {
		function sync() {
			setLiveReady(readReadyParam());
		}
		window.addEventListener(READY_CHANGE_EVENT, sync);
		window.addEventListener('popstate', sync);
		return () => {
			window.removeEventListener(READY_CHANGE_EVENT, sync);
			window.removeEventListener('popstate', sync);
		};
	}, []);

	const isReady = liveReady === 'true';
	const duration = reduceMotion ? 0 : 0.7;

	return (
		<LazyMotion features={domAnimation}>
			<main className="perspective-normal perspective-origin-left container flex min-h-safe-screen items-center py-10 lg:py-20">
				<Backlight className="w-[47%] shrink-0 max-md:hidden relative aspect-square">
					<>
						<Motion
							as="div"
							className="absolute inset-0"
							initial={false}
							animate={{
								opacity: isReady ? 0 : 1,
								scale: isReady ? 0.94 : 1,
								filter: isReady ? 'blur(12px)' : 'blur(0px)',
							}}
							transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
							aria-hidden={isReady}
						>
							<AstronautReadingLottie className="-scale-x-100" />
						</Motion>
						<Motion
							as="div"
							className="absolute inset-0"
							initial={false}
							animate={{
								opacity: isReady ? 1 : 0,
								scale: isReady ? 1 : 1.06,
								filter: isReady ? 'blur(0px)' : 'blur(12px)',
							}}
							transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
							aria-hidden={!isReady}
						>
							<AstronautMoonLottie />
						</Motion>
					</>
				</Backlight>
				<Contact syncReady />
			</main>
		</LazyMotion>
	);
}
